import { AddCircleOutline, RecordVoiceOverOutlined } from '@mui/icons-material';
import RateReviewOutlined from '@mui/icons-material/RateReviewOutlined';
import { CollapsibleBox } from '@/components/common/collapsible-box.tsx';
import type { SxProps, Theme } from '@mui/material';
import { Box, Tooltip, IconButton, Typography } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import AddEditContributorModal from '@/components/events/detail/tabs/contributors/tab-actions/add-edit-contributor';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { ContributorInvitationIcon } from '@/components/events/detail/tabs/contributors/contributor-invitation-icon.tsx';
import { ContributorInvitationExpandButton } from '@/components/events/detail/tabs/contributors/contributor-invitation-expand-button.tsx';
import RequestContributorMediaModal from '@/components/events/detail/tabs/contributors/request-contributor-media.modal.tsx';
import { CollapsibleGroup } from '@/components/common/collapsible-group';
import UpdateContributorForm from '../../contributors/update-contributor.form';
import { getContributorFlowRoleById } from '@/utils/contributor-extraction.utils';
import { useFlowModeratorsSynced } from '../moderators/use-flow-moderators-synced';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { useSearchParams } from 'react-router';
import { useCreateContributorFlowRoleSlotByEventSlotMutation } from '@/state/mutations/contributors/use-create-contributor-flow-role-slot-by-event-slot';
import { useTranslations } from '@/i18n/use-translations';
import { ContributorInviteStatusPanel } from '@/components/events/detail/tabs/contributors/contributor-invite-status-panel.tsx';
import { usePatchContributorRoleMutation } from '@/state/mutations/contributors/use-patch-contributor-role';
import { usePatchContributorRoleSlotMutation } from '@/state/mutations/contributors/use-patch-contributor-role-slot';
import { useQueryClient } from '@tanstack/react-query';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { useNotifications } from '@/hooks/use-notifications';
import ConfirmationModal from '@/components/common/confirmation-modal';
import {
  ModeratorDeleteHeaderButton,
  moderatorDeleteMenuPresentation,
  resolveModeratorCanDeleteFlowRole,
} from './moderator-delete-header-button';
import { resolveContributorLineRemoteSpeakerStatus } from '@/utils/remote-speaker-status.utils';
import { contributorItemInlineStatusIcons } from '@/components/events/detail/tabs/common/contributors/contributor-item-inline-status-icons';
import { ContributorItemOverflowMenu } from '@/components/events/detail/tabs/common/contributors/contributor-item-overflow-menu';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import { getFlowModeratorPublicationHints, resolvePlanFlowDto } from '@/utils/contributor-invite-publication.utils';
import type { ContributorListDisplayVariant } from '@/components/flows/detail/flow-detail.types';
import { RemoteSpeakerScopeModal } from '@/components/events/detail/tabs/common/contributors/remote-speaker-scope.modal';
import {
  RemoteSpeakerDayEntitlementModal,
  useRemoteSpeakerDayEntitlementModalState,
} from '@/components/events/detail/tabs/common/contributors/remote-speaker-day-entitlement.modal';
import {
  resolveFlowRemoteSpeakerConfigureEnabled,
  resolveFlowRemoteSpeakerMenuShowsRemote,
} from '@/utils/remote-speaker-flow-toggle.utils';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';
import { getOriginEventId } from '@/helpers/event.helper';
import { getContributorProfileReviewIconColor } from '@/components/events/detail/tabs/sessions/session-actors/speaker-profile-review-status';
import { ScheduleContributorHeaderAvatar } from '@/components/events/detail/tabs/sessions/session-actors/schedule-contributor-header-avatar';
import {
  buildPlaylistContributorRows,
  canReorderContributorPlaylist,
  getFlowModeratorsPlaylist,
  orderFlowModerators,
} from '@/helpers/contributor-playlist.helper';
import { SortablePlaylistContributorList } from '@/components/events/detail/tabs/common/contributors/sortable-playlist-contributor-list';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { REMOTE_SPEAKERSHIP_FEATURE } from '@/constants/feature-flags';

export type ModeratorListProps = {
  flow: FlowDto;
  eventSlotId?: string;
  displayVariant?: ContributorListDisplayVariant;
  sx?: SxProps<Theme>;
};

export const ModeratorList = ({
  flow,
  eventSlotId: eventSlotIdProp,
  displayVariant = 'default',
  sx,
}: ModeratorListProps) => {
  const [selectedContributor, setSelectedContributor] = useState<ContributorDto | undefined>();
  const [editModalProfileOnly, setEditModalProfileOnly] = useState(false);
  const [isEditContributorModalOpen, setIsEditContributorModalOpen] = useState(false);
  const [isRequestMediaModalOpen, setIsRequestMediaModalOpen] = useState(false);
  const [contributorToToggle, setContributorToToggle] = useState<ContributorDto | undefined>();
  const [isToggleVisibilityModalOpen, setIsToggleVisibilityModalOpen] = useState(false);
  const [isRemoteSpeakerScopeModalOpen, setIsRemoteSpeakerScopeModalOpen] = useState(false);
  const [remoteSpeakerScopeEnabled, setRemoteSpeakerScopeEnabled] = useState(true);
  const [isToggleAutoCreateModalOpen, setIsToggleAutoCreateModalOpen] = useState(false);
  const [confirmPending, setConfirmPending] = useState<{
    contributorId: string;
    action: 'visibility' | 'remote' | 'autoCreate';
  } | null>(null);
  const [deleteModalTarget, setDeleteModalTarget] = useState<
    { contributor: ContributorDto; roleId: string } | undefined
  >();

  const isConfirmPending = (contributorId: string, action: 'visibility' | 'remote' | 'autoCreate') =>
    confirmPending?.contributorId === contributorId && confirmPending.action === action;
  const [searchParams] = useSearchParams();
  const { mutateAsync: createFlowRoleSlotByEventSlot } = useCreateContributorFlowRoleSlotByEventSlotMutation();
  const { mutateAsync: patchContributorRole } = usePatchContributorRoleMutation();
  const { mutateAsync: patchContributorRoleSlot } = usePatchContributorRoleSlotMutation();
  const queryClient = useQueryClient();
  const notification = useNotifications();
  const t = useTranslations();
  const mode = useEventMode();
  const deleteDisabled = isOrganizeMode(mode);

  const normalizeId = (value?: unknown) => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if ('id' in value && typeof (value as { id?: string }).id === 'string') {
        return (value as { id: string }).id;
      }
      if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
        return (value as { _id: string })._id;
      }
      if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
        return (value as { $oid: string }).$oid;
      }
    }
    return undefined;
  };

  const eventSlotIdParam = (eventSlotIdProp ?? searchParams.get('eventSlotId')) || undefined;
  const eventSlotId = eventSlotIdParam || undefined;
  const showMasterRoleActions = !eventSlotIdParam;
  const forceMasterMode = !eventSlotIdParam;
  const { flowForRoles, flowEntityId } = useFlowModeratorsSynced(flow, eventSlotIdParam);
  const event = useEventStore((s) => s.event);
  const contributors = useEventStore((s) => s.contributors);
  const originEventId = event ? getOriginEventId(event) : undefined;
  const { data: permissions } = usePermissionsQuery();
  const isEventManager =
    permissions?.events?.[originEventId ?? '']?.includes(EVENT_PERMISSIONS.MANAGE_EVENT_ROLES) ?? false;
  const { hasAccess: hasRemoteSpeakershipAccess, isLoading: isRemoteSpeakershipAccessLoading } =
    useFeatureAccess(REMOTE_SPEAKERSHIP_FEATURE);
  const remoteSpeakershipAvailable = !isRemoteSpeakershipAccessLoading && hasRemoteSpeakershipAccess;
  const remoteSpeakerDayEntitlementModal = useRemoteSpeakerDayEntitlementModalState();
  const moderatorPlaylist = getFlowModeratorsPlaylist(flowForRoles);
  const canReorderModerators = canReorderContributorPlaylist(moderatorPlaylist);
  const orderedModerators = useMemo(
    () => orderFlowModerators(flowForRoles, contributors || [], event?.multiFlow),
    [flowForRoles, contributors, event?.multiFlow],
  );
  const moderatorPlaylistRows = useMemo(
    () =>
      buildPlaylistContributorRows(moderatorPlaylist, orderedModerators, (contributor) => {
        const flowRole = getContributorFlowRoleById(contributor, flowEntityId, 'moderator');
        if (flowRole?.id) return flowRole.id;
        if (event?.multiFlow === false) {
          return contributor.roles.find((role) => role.role === 'moderator' && role.entityType === 'Event')?.id;
        }
        return undefined;
      }),
    [moderatorPlaylist, orderedModerators, flowEntityId, event?.multiFlow],
  );
  const isFlowSlotAssetsUi = displayVariant === 'flowSlotAssets';
  const isFlowScheduleInlineUi = displayVariant === 'flowScheduleInline';
  const scheduleInlineRow = isFlowScheduleInlineUi;

  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventFlowsPlan = useEventStore((s) => s.eventFlowsPlan);
  const eventFlowsOrganize = useEventStore((s) => s.eventFlowsOrganize);
  const planFlowForInvite = useMemo(
    () => resolvePlanFlowDto(flowForRoles, eventFlowsPlan, eventFlowsOrganize),
    [flowForRoles, eventFlowsPlan, eventFlowsOrganize],
  );
  const flowModeratorPublicationHints = useMemo(
    () =>
      getFlowModeratorPublicationHints({
        planFlow: planFlowForInvite,
        eventPlan,
        eventSlotId: eventSlotIdParam,
        eventFlowsOrganize,
      }),
    [planFlowForInvite, eventPlan, eventSlotIdParam, eventFlowsOrganize],
  );

  const openContributorEditModal = (contributor: ContributorDto, profileOnlyModal = false) => {
    setSelectedContributor(contributor);
    setEditModalProfileOnly(profileOnlyModal);
    setIsEditContributorModalOpen(true);
  };

  const handleEditContributor = (e: React.MouseEvent<Element>, contributor: ContributorDto) => {
    e.stopPropagation();
    openContributorEditModal(contributor, false);
  };

  const selectedModeratorRole = useMemo(() => {
    if (!selectedContributor) return undefined;
    return getContributorFlowRoleById(selectedContributor, flowEntityId, 'moderator');
  }, [selectedContributor, flowEntityId]);

  const handleToggleVisibilityConfirm = async () => {
    if (!contributorToToggle) return;
    setConfirmPending({ contributorId: contributorToToggle.id, action: 'visibility' });
    try {
      const role = getContributorFlowRoleById(contributorToToggle, flowEntityId, 'moderator');
      if (!role) return;

      const roleSlot =
        eventSlotIdParam && role.slots
          ? role.slots.find(
              (slot) =>
                slot.entityType === 'Flow' &&
                normalizeId(slot.eventSlotId) === eventSlotIdParam &&
                slot.deleted !== true,
            )
          : undefined;
      const currentValue = eventSlotIdParam ? roleSlot?.isVisible !== false : role.isVisible !== false;
      const newValue = !currentValue;

      if (eventSlotIdParam && roleSlot?.id) {
        await patchContributorRoleSlot({
          contributorId: contributorToToggle.id,
          roleId: role.id,
          slotId: roleSlot.id,
          isVisible: newValue,
        });
      } else {
        await patchContributorRole({
          contributorId: contributorToToggle.id,
          roleId: role.id,
          isVisible: newValue,
        });
      }

      await queryClient.refetchQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] });

      const displayName = contributorToToggle.profiles?.[0]?.displayName || contributorToToggle.email || 'Contributor';
      notification.notifySuccess(
        newValue
          ? t('session.visibility.enabledNotification', { name: displayName })
          : t('session.visibility.disabledNotification', { name: displayName }),
      );
    } catch (error) {
      console.error(error);
      const displayName =
        contributorToToggle?.profiles?.[0]?.displayName || contributorToToggle?.email || 'Contributor';
      notification.notifyError(t('session.visibility.toggleError', { name: displayName }));
    } finally {
      setConfirmPending(null);
      setIsToggleVisibilityModalOpen(false);
      setContributorToToggle(undefined);
    }
  };

  const handleToggleRemoteSpeakerClose = () => {
    setConfirmPending(null);
    setIsRemoteSpeakerScopeModalOpen(false);
    setContributorToToggle(undefined);
  };

  const findModeratorRoleSlotForEventSlot = (
    role: ContributorDto['roles'][number] | undefined,
    eventSlot: string | undefined,
  ) => {
    if (!role?.slots || !eventSlot) return undefined;
    return role.slots.find(
      (slot) => slot.entityType === 'Flow' && normalizeId(slot.eventSlotId) === eventSlot && slot.deleted !== true,
    );
  };

  const handleToggleAutoCreateConfirm = async () => {
    if (!contributorToToggle) return;
    setConfirmPending({ contributorId: contributorToToggle.id, action: 'autoCreate' });
    try {
      const role = getContributorFlowRoleById(contributorToToggle, flowEntityId, 'moderator');
      if (!role) return;

      const currentValue = role.autoCreateSlots !== false;
      const newValue = !currentValue;

      await patchContributorRole({
        contributorId: contributorToToggle.id,
        roleId: role.id,
        autoCreateSlots: newValue,
      });

      await queryClient.refetchQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] });

      notification.notifySuccess(t('session.notification.toggleAutoCreateSlotsSuccess'));
    } catch (error) {
      console.error(error);
      notification.notifyError(t('session.notification.toggleAutoCreateSlotsError'));
    } finally {
      setConfirmPending(null);
      setIsToggleAutoCreateModalOpen(false);
      setContributorToToggle(undefined);
    }
  };

  return (
    <>
      {orderedModerators.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, px: 1, py: 2 }}>
          <RecordVoiceOverOutlined sx={{ fontSize: 28, color: 'action.disabled' }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('common.moderatorEmpty')}
          </Typography>
        </Box>
      )}
      <CollapsibleGroup>
        {(() => {
          const renderModerator = (contributor: ContributorDto, dragHandle?: ReactNode) => {
            const moderatorRole = getContributorFlowRoleById(contributor, flowEntityId, 'moderator');
            if (!moderatorRole) return null;
            const hasActiveRoleSlot = eventSlotId
              ? moderatorRole.slots?.some(
                  (slot) =>
                    slot.entityType === 'Flow' &&
                    normalizeId(slot.eventSlotId) === eventSlotId &&
                    slot.deleted !== true,
                )
              : true;
            const roleHasNoSlots = !moderatorRole.slots?.some((s) => s.deleted !== true);
            const roleSlotForEventSlot = eventSlotId
              ? moderatorRole.slots?.find(
                  (slot) =>
                    slot.entityType === 'Flow' &&
                    normalizeId(slot.eventSlotId) === eventSlotId &&
                    slot.deleted !== true,
                )
              : undefined;
            const isInactiveInSlot = !hasActiveRoleSlot;
            const slotRestoreAction = isInactiveInSlot
              ? [
                  {
                    icon: <AddCircleOutline fontSize="small" />,
                    tooltip: t('event.detail.contributors.createRoleSlot'),
                    onClick: () => {
                      if (!eventSlotId) return;
                      createFlowRoleSlotByEventSlot({
                        contributorId: contributor.id,
                        roleId: moderatorRole.id,
                        eventSlotId,
                      });
                    },
                  },
                ]
              : [];

            const remoteSpeakerConfigured = contributor.remoteSpeakerEnabled ?? false;
            const remoteLineStatus = resolveContributorLineRemoteSpeakerStatus({
              contributor,
              role: moderatorRole,
              roleSlot: roleSlotForEventSlot,
              event: event ?? eventPlan ?? undefined,
              eventSlotId,
              useRoleAcceptance: !eventSlotIdParam,
            });
            const menuShowsRemote = resolveFlowRemoteSpeakerMenuShowsRemote({
              role: moderatorRole,
              roleSlot: roleSlotForEventSlot,
              useSlotContext: Boolean(eventSlotIdParam && roleSlotForEventSlot),
              contributorRemoteSpeakerEnabled: remoteSpeakerConfigured,
            });
            const visibilityEnabled = eventSlotIdParam
              ? roleSlotForEventSlot?.isVisible !== false
              : moderatorRole.isVisible !== false;
            const showContributorActions = !isInactiveInSlot;
            const showRemoteAndVisibility = showContributorActions;
            const showRemoteSpeakerControls = remoteSpeakershipAvailable && showRemoteAndVisibility;
            const labelContext = eventSlotIdParam ? ('flowSlot' as const) : ('flowRole' as const);

            const onToggleRemoteSpeaker = () => {
              setContributorToToggle(contributor);
              const enabling = resolveFlowRemoteSpeakerConfigureEnabled({
                role: moderatorRole,
                roleSlot: roleSlotForEventSlot,
                useSlotContext: Boolean(eventSlotIdParam && roleSlotForEventSlot),
                contributorRemoteSpeakerEnabled: remoteSpeakerConfigured,
              });
              setRemoteSpeakerScopeEnabled(enabling);
              setIsRemoteSpeakerScopeModalOpen(true);
            };
            const onToggleVisibility = () => {
              setContributorToToggle(contributor);
              setIsToggleVisibilityModalOpen(true);
            };

            const onRemoteSpeakerStatusClick =
              showRemoteSpeakerControls && remoteLineStatus.dayStatus === 'pending' && remoteLineStatus.dayId
                ? () => {
                    remoteSpeakerDayEntitlementModal.openForContributorDay(
                      contributor,
                      remoteLineStatus.dayId,
                      eventPlan ?? event,
                    );
                  }
                : undefined;

            const inlineStatusIcons = contributorItemInlineStatusIcons({
              t,
              labelContext,
              showRemoteSpeaker: showRemoteSpeakerControls && remoteLineStatus.showIcon,
              remoteSpeakerEnabled: remoteLineStatus.showIcon,
              remoteSpeakerIconColor: remoteLineStatus.color ?? 'info',
              onRemoteSpeakerStatusClick,
              remoteSpeakerStatusClickable: showRemoteSpeakerControls && remoteLineStatus.dayStatus === 'pending',
              showVisibility: showRemoteAndVisibility,
              visibilityEnabled,
              onToggleVisibility: showRemoteAndVisibility ? onToggleVisibility : undefined,
              remoteSpeakerLoading: isConfirmPending(contributor.id, 'remote'),
              visibilityLoading: isConfirmPending(contributor.id, 'visibility'),
              disabled: confirmPending?.contributorId === contributor.id,
            });
            const profileReviewAction = scheduleInlineRow ? (
              <Tooltip
                key="profile-review"
                title={t('session.scheduleActor.profileReviewTooltip')}
                placement="left"
                disableInteractive
              >
                <IconButton
                  size="small"
                  color={getContributorProfileReviewIconColor(contributor)}
                  onClick={(e) => {
                    e.stopPropagation();
                    openContributorEditModal(contributor, true);
                  }}
                  sx={{
                    '& > svg': {
                      fontSize: '1.5rem',
                      width: '1.5rem',
                      height: '1.5rem',
                    },
                  }}
                >
                  <RateReviewOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null;

            const moderatorDeletePresentation = moderatorDeleteMenuPresentation(
              resolveModeratorCanDeleteFlowRole({
                forceMasterMode,
                eventSlotIdParam,
                flowEntityId,
                flowForDelete: flowForRoles,
                eventPlanSlots: eventPlan?.slots,
              }),
            );

            const overflowMenu = showContributorActions ? (
              <ContributorItemOverflowMenu
                labelContext={labelContext}
                showAutoCreate={showMasterRoleActions}
                autoCreateEnabled={moderatorRole.autoCreateSlots !== false}
                onToggleAutoCreate={() => {
                  setContributorToToggle(contributor);
                  setIsToggleAutoCreateModalOpen(true);
                }}
                showRemoteSpeaker={showRemoteSpeakerControls}
                remoteSpeakerEnabled={menuShowsRemote}
                onToggleRemoteSpeaker={onToggleRemoteSpeaker}
                showVisibility={showRemoteAndVisibility}
                visibilityEnabled={visibilityEnabled}
                onToggleVisibility={onToggleVisibility}
                showDelete
                deleteAction={moderatorDeletePresentation.deleteAction}
                deleteTarget={moderatorDeletePresentation.deleteTarget}
                deleteDisabled={deleteDisabled}
                onDelete={() => setDeleteModalTarget({ contributor, roleId: moderatorRole.id })}
              />
            ) : null;

            const headerActionButtons = isInactiveInSlot
              ? slotRestoreAction
              : overflowMenu
                ? [
                    {
                      icon: overflowMenu,
                      unwrapped: true,
                      testId: `contributor-overflow-${contributor.id}`,
                    },
                  ]
                : [];

            return (
              <CollapsibleBox
                sx={{
                  ...sx,
                  ...(isInactiveInSlot
                    ? {
                        opacity: 0.5,
                        pointerEvents: 'auto',
                      }
                    : {}),
                }}
                key={contributor.id}
                id={contributor.id}
                title={contributor.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor')}
                onClick={
                  isInactiveInSlot
                    ? undefined
                    : scheduleInlineRow
                      ? undefined
                      : (e) => handleEditContributor(e, contributor)
                }
                iconStart={
                  <>
                    {dragHandle}
                    {isFlowScheduleInlineUi ? (
                      <ScheduleContributorHeaderAvatar
                        contributor={contributor}
                        onClick={() => openContributorEditModal(contributor, true)}
                      />
                    ) : (
                      <ContributorInvitationExpandButton
                        itemId={contributor.id}
                        color={getContributorProfileReviewIconColor(contributor)}
                        sx={{
                          'p': 0.25,
                          'borderRadius': 1,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <ContributorInvitationIcon
                          contributor={contributor}
                          flowDetailsInvite={
                            eventSlotId
                              ? {
                                  roleAccepted: Boolean(moderatorRole.acceptedAt),
                                  roleInvited: Boolean(moderatorRole.invitedAt),
                                  roleStagedForInvite: moderatorRole.stagedForInvite === true,
                                  slotInvited: Boolean(roleSlotForEventSlot?.invitedAt),
                                  slotAccepted: roleHasNoSlots || Boolean(roleSlotForEventSlot?.acceptedAt),
                                  slotStagedForInvite: roleSlotForEventSlot?.stagedForInvite === true,
                                }
                              : undefined
                          }
                        />
                      </ContributorInvitationExpandButton>
                    )}
                  </>
                }
                iconsEnd={
                  isInactiveInSlot ? [] : [...inlineStatusIcons, ...(profileReviewAction ? [profileReviewAction] : [])]
                }
                actionButtons={headerActionButtons}
                showCollapseArrow={!isInactiveInSlot}
                hideCollapseExpandTooltip
              >
                {!isInactiveInSlot && (
                  <UpdateContributorForm
                    contributor={contributor}
                    entityId={flowEntityId}
                    entityType="Flow"
                    forceMasterMode={forceMasterMode}
                    eventSlotIdOverride={eventSlotIdParam || undefined}
                    suppressWidgetIconButtons
                    showEmailField={!scheduleInlineRow}
                    fieldsLayout={scheduleInlineRow ? 'scheduleActor' : isFlowSlotAssetsUi ? 'emailOnly' : 'full'}
                    inviteStatusPanel={
                      !isFlowScheduleInlineUi ? (
                        <ContributorInviteStatusPanel
                          key={`${contributor.id}-${moderatorRole.id}-${roleSlotForEventSlot?.id ?? 'no-slot'}`}
                          contributor={contributor}
                          roleId={moderatorRole.id}
                          roleInvited={Boolean(moderatorRole.invitedAt)}
                          roleAccepted={Boolean(moderatorRole.acceptedAt)}
                          publicationBlockContribution={flowModeratorPublicationHints.contribution}
                          publicationBlockTimeSlot={flowModeratorPublicationHints.timeSlot}
                          displayVariant={isFlowSlotAssetsUi ? 'flowSlotAssets' : 'default'}
                          timeSlot={
                            eventSlotId && roleSlotForEventSlot
                              ? {
                                  roleSlotId: roleSlotForEventSlot.id,
                                  invited: Boolean(roleSlotForEventSlot.invitedAt),
                                  accepted: Boolean(roleSlotForEventSlot.acceptedAt),
                                }
                              : null
                          }
                        />
                      ) : undefined
                    }
                  />
                )}
              </CollapsibleBox>
            );
          };

          if (canReorderModerators && moderatorPlaylist?.id && moderatorPlaylistRows.length >= 2) {
            return (
              <SortablePlaylistContributorList
                rows={moderatorPlaylistRows}
                playlistId={moderatorPlaylist.id}
                canReorder={canReorderModerators}
              >
                {(row, dragHandle) => renderModerator(row.contributor, dragHandle)}
              </SortablePlaylistContributorList>
            );
          }

          return orderedModerators.map((contributor) => renderModerator(contributor));
        })()}
      </CollapsibleGroup>
      <AddEditContributorModal
        contributor={selectedContributor}
        open={isEditContributorModalOpen}
        setOpen={setIsEditContributorModalOpen}
        eventSlotId={eventSlotId}
        profileOnly={editModalProfileOnly}
      />
      {selectedModeratorRole && (
        <RequestContributorMediaModal
          entity={flowForRoles}
          entityType="Flow"
          contributor={selectedContributor}
          isMediaRequested={selectedModeratorRole?.isMediaRequested ?? false}
          role={selectedModeratorRole}
          open={isRequestMediaModalOpen}
          setOpen={setIsRequestMediaModalOpen}
        />
      )}
      <ConfirmationModal
        open={isToggleVisibilityModalOpen}
        isLoading={confirmPending?.action === 'visibility'}
        title={t(
          eventSlotIdParam
            ? 'flows.detail.contributors.visibility.toggleTitleSlot'
            : 'flows.detail.contributors.visibility.toggleTitleRole',
          {
            name:
              contributorToToggle?.profiles?.[0]?.displayName ||
              contributorToToggle?.email ||
              t('event.detail.contributors.unknownContributor'),
          },
        )}
        text={
          contributorToToggle
            ? (() => {
                const role = getContributorFlowRoleById(contributorToToggle, flowEntityId, 'moderator');
                if (!role) return '';
                const roleSlot =
                  eventSlotIdParam && role.slots
                    ? role.slots.find(
                        (slot) =>
                          slot.entityType === 'Flow' &&
                          normalizeId(slot.eventSlotId) === eventSlotIdParam &&
                          slot.deleted !== true,
                      )
                    : undefined;
                const isVisible = eventSlotIdParam ? roleSlot?.isVisible !== false : role.isVisible !== false;
                return isVisible
                  ? t(
                      eventSlotIdParam
                        ? 'flows.detail.contributors.visibility.disableConfirmationSlot'
                        : 'flows.detail.contributors.visibility.disableConfirmationRole',
                      {
                        name:
                          contributorToToggle.profiles?.[0]?.displayName ||
                          contributorToToggle.email ||
                          t('event.detail.contributors.unknownContributor'),
                      },
                    )
                  : t(
                      eventSlotIdParam
                        ? 'flows.detail.contributors.visibility.enableConfirmationSlot'
                        : 'flows.detail.contributors.visibility.enableConfirmationRole',
                      {
                        name:
                          contributorToToggle.profiles?.[0]?.displayName ||
                          contributorToToggle.email ||
                          t('event.detail.contributors.unknownContributor'),
                      },
                    );
              })()
            : ''
        }
        onConfirm={handleToggleVisibilityConfirm}
        onCancel={() => {
          if (confirmPending) return;
          setIsToggleVisibilityModalOpen(false);
        }}
      />
      {remoteSpeakershipAvailable && contributorToToggle ? (
        <RemoteSpeakerScopeModal
          open={isRemoteSpeakerScopeModalOpen}
          onClose={handleToggleRemoteSpeakerClose}
          contributor={contributorToToggle}
          enabled={remoteSpeakerScopeEnabled}
          flowId={flowEntityId}
          roleId={getContributorFlowRoleById(contributorToToggle, flowEntityId, 'moderator')?.id ?? ''}
          slotId={
            eventSlotIdParam
              ? findModeratorRoleSlotForEventSlot(
                  getContributorFlowRoleById(contributorToToggle, flowEntityId, 'moderator'),
                  eventSlotIdParam,
                )?.id
              : undefined
          }
          showSlotOption={Boolean(eventSlotIdParam)}
        />
      ) : null}
      {remoteSpeakershipAvailable && remoteSpeakerDayEntitlementModal.contributor ? (
        <RemoteSpeakerDayEntitlementModal
          open={remoteSpeakerDayEntitlementModal.open}
          onClose={remoteSpeakerDayEntitlementModal.close}
          contributor={remoteSpeakerDayEntitlementModal.contributor}
          dayIds={remoteSpeakerDayEntitlementModal.dayIds}
          dayLabel={remoteSpeakerDayEntitlementModal.dayLabel}
          isEventManager={isEventManager}
        />
      ) : null}
      <ConfirmationModal
        open={isToggleAutoCreateModalOpen}
        isLoading={confirmPending?.action === 'autoCreate'}
        title={
          contributorToToggle
            ? getContributorFlowRoleById(contributorToToggle, flowEntityId, 'moderator')?.autoCreateSlots !== false
              ? t('flows.detail.contributors.autoCreateSlots.disableTitle', {
                  name:
                    contributorToToggle.profiles?.[0]?.displayName ||
                    contributorToToggle.email ||
                    t('event.detail.contributors.unknownContributor'),
                })
              : t('flows.detail.contributors.autoCreateSlots.enableTitle', {
                  name:
                    contributorToToggle?.profiles?.[0]?.displayName ||
                    contributorToToggle?.email ||
                    t('event.detail.contributors.unknownContributor'),
                })
            : ''
        }
        text={
          contributorToToggle
            ? getContributorFlowRoleById(contributorToToggle, flowEntityId, 'moderator')?.autoCreateSlots !== false
              ? t('flows.detail.contributors.autoCreateSlots.disableConfirmation', {
                  name:
                    contributorToToggle.profiles?.[0]?.displayName ||
                    contributorToToggle.email ||
                    t('event.detail.contributors.unknownContributor'),
                })
              : t('flows.detail.contributors.autoCreateSlots.enableConfirmation', {
                  name:
                    contributorToToggle?.profiles?.[0]?.displayName ||
                    contributorToToggle?.email ||
                    t('event.detail.contributors.unknownContributor'),
                })
            : ''
        }
        onConfirm={handleToggleAutoCreateConfirm}
        onCancel={() => {
          if (confirmPending) return;
          setIsToggleAutoCreateModalOpen(false);
        }}
      />
      {deleteModalTarget ? (
        <ModeratorDeleteHeaderButton
          hideTrigger
          open={Boolean(deleteModalTarget)}
          onOpenChange={(open) => {
            if (!open) setDeleteModalTarget(undefined);
          }}
          flowEntityId={flowEntityId}
          flow={flowForRoles}
          contributor={deleteModalTarget.contributor}
          roleId={deleteModalTarget.roleId}
          eventSlotIdParam={eventSlotIdParam}
          forceMasterMode={forceMasterMode}
        />
      ) : null}
    </>
  );
};
